/* mw-tabs — accessible tabs with optional autoplay (docs/17 §5). Light DOM, no dependencies.

   Markup (the section renders it; styles in assets/tabs.css):
   <mw-tabs class="tabs tabs--pill|underline|text" data-autoplay="6" data-activate="click|hover"
            [data-transition="fade|crossfade|none"]>
     <div class="tabs__list" role="tablist" aria-label="…">
       <button type="button" class="tabs__tab" role="tab" id="T-1" aria-controls="P-1"
               aria-selected="true" tabindex="0">…<span class="tabs__progress" aria-hidden="true"></span></button> …
     </div>
     [<button type="button" class="tabs__play" data-tabs-play data-label-pause="…" data-label-play="…" aria-label="…">…</button>]
     [<div class="tabs__panels">]
       <div class="tabs__panel" role="tabpanel" id="P-1" aria-labelledby="T-1" tabindex="0">…</div>
       <div class="tabs__panel" role="tabpanel" id="P-2" aria-labelledby="T-2" tabindex="0" hidden>…</div>
     [</div>]
   </mw-tabs>

   Behaviour
   - Click (or hover with data-activate="hover" on pointer devices) selects a tab. Arrow keys move
     and select (automatic activation; Right/Down next, Left/Up previous, mirrored in RTL), Home/End.
   - data-autoplay (seconds, 0 = off) rotates the tabs. It runs only when a [data-tabs-play] button
     exists (WCAG 2.2.2) and never under reduced motion. The active tab's --tab-progress runs 0 → 1
     as a CSS animation (tabs.css); its animationend advances the tabs, so a pause keeps the exact
     remaining time. Pauses: the play button, hover (panels; the tab list too in click mode),
     keyboard focus, off screen, a hidden browser tab and a block selected in the theme editor.
   - Panels fade in (--dur). data-transition="crossfade" keeps the old panel visible while it fades
     out — stack the panels in .tabs__panels (tabs.css) so the height does not jump.
   - Without JS, tabs.css hides the tab list and shows every panel.

   Events: mw:tabs:change on the element (bubbles), detail = { index, tab, panel, source }.
   API on the element: select(index, { source }), next(), pause(reason), resume(reason), index. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const PROGRESS = 'mw-tab-progress';

class MwTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = this.own('.tabs__tab');
    this.panels = this.own('.tabs__panel');
    if (!this.tabs.length) return;

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.list = this.own('.tabs__list')[0] ?? this.tabs[0].parentElement;
    this.playButton = this.own('[data-tabs-play]')[0] ?? null;
    this.interval = Math.max(0, Number.parseFloat(this.dataset.autoplay) || 0);
    this.pauses = new Set();

    const selected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.current = -1;
    this.select(Math.max(0, selected), { source: 'init', silent: true, instant: true });
    this.classList.add('is-ready');

    this.tabs.forEach((tab, index) => {
      tab.addEventListener(
        'click',
        (event) => {
          // A link inside a tab (e.g. «View collection») keeps its own behaviour.
          if (event.target instanceof Element && event.target.closest('a[href]') && event.target.closest('a[href]') !== tab) return;
          this.select(index, { source: 'click' });
        },
        { signal }
      );
      if (this.dataset.activate === 'hover') {
        tab.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType === 'mouse' && finePointer.matches) this.select(index, { source: 'hover' });
          },
          { signal }
        );
      }
    });

    this.list.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

    this.setupAutoplay(signal);
    this.setupEditor(signal);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.visibilityObserver?.disconnect();
    clearTimeout(this.leaveTimer);
  }

  /* Elements that belong to this tab set, not to a nested one. */
  own(selector) {
    return [...this.querySelectorAll(selector)].filter((el) => el.closest('mw-tabs') === this);
  }

  get index() {
    return this.current;
  }

  get rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  /* ---------- Selection ---------- */

  select(index, { source = 'api', silent = false, instant = false } = {}) {
    const count = this.tabs.length;
    const next = ((index % count) + count) % count;
    if (next === this.current) return;
    const previous = this.current;
    this.current = next;

    this.tabs.forEach((tab, i) => {
      const on = i === next;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
    });

    const animate = !instant && previous >= 0 && !reducedMotion.matches && this.dataset.transition !== 'none';
    this.finishLeave();
    this.panels.forEach((panel, i) => {
      if (i === next) {
        panel.hidden = false;
        panel.removeAttribute('inert');
        panel.classList.remove('is-entering');
        if (animate) {
          // Restart the entrance even if the panel was shown a moment ago.
          void panel.offsetWidth;
          panel.classList.add('is-entering');
          const entered = (event) => {
            // animationend bubbles: ignore animations of the panel's children.
            if (event.target !== panel) return;
            panel.removeEventListener('animationend', entered);
            panel.classList.remove('is-entering');
          };
          panel.addEventListener('animationend', entered);
        }
      } else if (i === previous && animate && this.dataset.transition === 'crossfade') {
        this.leave(panel);
      } else {
        panel.hidden = true;
      }
    });

    this.revealTab(this.tabs[next], instant);
    if (!silent) {
      this.dispatchEvent(
        new CustomEvent('mw:tabs:change', {
          bubbles: true,
          detail: { index: next, tab: this.tabs[next], panel: this.panels[next] ?? null, source },
        })
      );
    }
  }

  next() {
    this.select(this.current + 1, { source: 'autoplay' });
  }

  /* Crossfade: the old panel stays in place, inert, while it fades out. */
  leave(panel) {
    this.leaving = panel;
    panel.setAttribute('inert', '');
    panel.classList.add('is-leaving');
    const done = (event) => {
      if (event && event.target !== panel) return;
      panel.removeEventListener('animationend', done); // also on the timeout path
      this.finishLeave();
    };
    panel.addEventListener('animationend', done);
    this.leaveTimer = setTimeout(done, 1000);
  }

  finishLeave() {
    clearTimeout(this.leaveTimer);
    const panel = this.leaving;
    if (!panel) return;
    this.leaving = null;
    panel.classList.remove('is-leaving');
    panel.removeAttribute('inert');
    if (this.panels.indexOf(panel) !== this.current) panel.hidden = true;
  }

  /* Keep the selected tab in view when the list scrolls sideways (mobile pills). Never scrolls the page. */
  revealTab(tab, instant) {
    const list = this.list;
    if (!list || list.scrollWidth <= list.clientWidth + 1) return;
    const listBox = list.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const offset = tabBox.left - listBox.left + list.scrollLeft;
    const left = offset - (list.clientWidth - tabBox.width) / 2;
    list.scrollTo({ left, behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
  }

  onKeydown(event) {
    const tab = event.target instanceof Element ? event.target.closest('.tabs__tab') : null;
    const from = this.tabs.indexOf(tab);
    if (from < 0) return;
    const forward = this.rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = this.rtl ? 'ArrowRight' : 'ArrowLeft';
    let to;
    switch (event.key) {
      case forward:
      case 'ArrowDown':
        to = from + 1;
        break;
      case back:
      case 'ArrowUp':
        to = from - 1;
        break;
      case 'Home':
        to = 0;
        break;
      case 'End':
        to = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        // Buttons click themselves and links inside a tab follow; a div[role=tab] needs this.
        if (tab.tagName === 'BUTTON' || event.target !== tab) return;
        to = from;
        break;
      default:
        return;
    }
    event.preventDefault();
    const count = this.tabs.length;
    to = ((to % count) + count) % count;
    this.select(to, { source: 'keyboard' });
    this.tabs[to].focus();
  }

  /* ---------- Autoplay ---------- */

  setupAutoplay(signal) {
    if (!this.interval || !this.playButton) {
      if (this.playButton) this.playButton.hidden = true;
      return;
    }
    this.style.setProperty('--tabs-interval', `${this.interval}s`);

    this.playButton.addEventListener(
      'click',
      () => (this.pauses.has('user') ? this.resume('user') : this.pause('user')),
      { signal }
    );

    this.addEventListener(
      'animationend',
      (event) => {
        if (event.animationName !== PROGRESS || event.target !== this.tabs[this.current]) return;
        if (this.classList.contains('is-autoplay') && !this.pauses.size) this.next();
      },
      { signal }
    );

    // Hover pauses over the panels; in click mode the tab list counts too (the pointer is about to
    // choose). In hover mode, moving over the tabs selects them and restarts the progress instead.
    const hoverAreas = [...this.panels];
    if (this.dataset.activate !== 'hover') hoverAreas.push(this.list);
    hoverAreas.forEach((area) => {
      area.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.pause('hover'), { signal });
      area.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && this.resume('hover'), { signal });
    });

    // Keyboard focus only: a mouse click on a tab focuses it too, and must not stop the rotation.
    this.addEventListener(
      'focusin',
      (event) => {
        const el = event.target;
        if (el !== this.playButton && el instanceof Element && el.matches(':focus-visible')) this.pause('focus');
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget) || event.relatedTarget === this.playButton) {
          this.resume('focus');
        }
      },
      { signal }
    );

    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause('hidden') : this.resume('hidden')), {
      signal,
    });
    reducedMotion.addEventListener('change', () => this.syncAutoplay(), { signal });

    if ('IntersectionObserver' in window) {
      this.pauses.add('offscreen');
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')),
        { threshold: 0.1 }
      );
      this.visibilityObserver.observe(this);
    }

    this.syncAutoplay();
  }

  syncAutoplay() {
    const on = this.interval > 0 && !!this.playButton && !reducedMotion.matches;
    this.classList.toggle('is-autoplay', on);
    if (this.playButton) this.playButton.hidden = !on;
    this.renderPause();
  }

  pause(reason) {
    this.pauses.add(reason);
    this.renderPause();
  }

  resume(reason) {
    this.pauses.delete(reason);
    this.renderPause();
  }

  renderPause() {
    this.classList.toggle('is-paused', this.pauses.size > 0);
    const button = this.playButton;
    if (!button) return;
    const paused = this.pauses.has('user');
    button.toggleAttribute('data-paused', paused);
    const label = paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
  }

  /* ---------- Theme editor ---------- */

  setupEditor(signal) {
    if (!window.Shopify?.designMode) return;
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element) || !this.contains(target)) return;
        const index = this.indexFor(target);
        if (index < 0) return;
        this.pause('editor');
        this.select(index, { source: 'editor', instant: true });
      },
      { signal }
    );
    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (event.target instanceof Element && this.contains(event.target)) this.resume('editor');
      },
      { signal }
    );
  }

  /* The tab a selected block belongs to: its panel, its tab, a panel inside it, or data-tabs-index. */
  indexFor(target) {
    const panel = target.closest('.tabs__panel');
    if (panel && this.panels.includes(panel)) return this.panels.indexOf(panel);
    const tab = target.closest('.tabs__tab');
    if (tab && this.tabs.includes(tab)) return this.tabs.indexOf(tab);
    const inner = this.panels.findIndex((p) => target.contains(p));
    if (inner >= 0) return inner;
    const marked = target.closest('[data-tabs-index]');
    if (marked && this.contains(marked)) return Number(marked.dataset.tabsIndex) || 0;
    return -1;
  }
}

if (!customElements.get('mw-tabs')) customElements.define('mw-tabs', MwTabs);

export { MwTabs };
